import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db, storage } from '../../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Plus, Trash2, Edit2, Save, X, Image as ImageIcon, FileText, Upload, Loader, ExternalLink } from 'lucide-react';
import { triggerAutoUpdate } from '../../utils/sitemapService';

const ResourceManager = () => {
    const [resources, setResources] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [currentResource, setCurrentResource] = useState(null);
    const [uploading, setUploading] = useState(false);

    // START SEED DATA
    const seedResources = async () => {
        if (!window.confirm("Add sample resources?")) return;
        setIsLoading(true);
        try {
            const samples = [
                {
                    title: "Scaling Customer Support for E-commerce Giants",
                    summary: "How we helped a top-tier retailer reduce response times by 40% while cutting costs.",
                    category: "Case Study",
                    image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&q=80&w=1000", // Updated working image
                    file_url: "",
                    date_published: new Date().toISOString().split('T')[0],
                    createdAt: new Date().toISOString()
                },
                {
                    title: "The State of Outsourcing 2026",
                    summary: "An in-depth whitepaper on the shifting landscape of global talent and AI integration.",
                    category: "Whitepaper",
                    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=1000",
                    file_url: "",
                    date_published: new Date().toISOString().split('T')[0],
                    createdAt: new Date().toISOString()
                },
                {
                    title: "BPO Cost Savings Calculator",
                    summary: "Calculate your potential savings by outsourcing specific roles to the Philippines.",
                    category: "Calculator",
                    image: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&q=80&w=1000",
                    file_url: "",
                    date_published: new Date().toISOString().split('T')[0],
                    createdAt: new Date().toISOString()
                }
            ];

            for (const item of samples) {
                await addDoc(collection(db, 'cases'), item);
            }
            fetchResources();
            alert("Sample resources added!");
        } catch (error) {
            console.error("Error seeding resources:", error);
        } finally {
            setIsLoading(false);
        }
    };
    // END SEED DATA

    const initialResourceState = {
        title: '',
        summary: '',
        content: '', // New field for full case study content
        category: 'Case Study', // Case Study, Whitepaper, Calculator
        image: '',
        file_url: '',
        date_published: new Date().toISOString().split('T')[0]
    };

    const [formData, setFormData] = useState(initialResourceState);

    useEffect(() => {
        fetchResources();
    }, []);

    const fetchResources = async () => {
        setIsLoading(true);
        try {
            const q = query(collection(db, 'cases'), orderBy('date_published', 'desc'));
            const querySnapshot = await getDocs(q);
            const data = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setResources(data);
        } catch (error) {
            console.error("Error fetching resources:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileUpload = async (e, field) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        try {
            const storageRef = ref(storage, `resources/${Date.now()}_${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            setFormData(prev => ({ ...prev, [field]: downloadURL }));
        } catch (error) {
            console.error("Error uploading file:", error);
            alert("Upload failed. Please use a URL instead.");
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            if (currentResource) {
                await updateDoc(doc(db, 'cases', currentResource.id), formData);
            } else {
                await addDoc(collection(db, 'cases'), {
                    ...formData,
                    createdAt: new Date().toISOString()
                });
            }
            setIsEditing(false);
            setCurrentResource(null);
            setFormData(initialResourceState);
            fetchResources();
            // Trigger Sitemap Update
            await triggerAutoUpdate();
        } catch (error) {
            console.error("Error saving resource:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (res) => {
        setCurrentResource(res);
        setFormData(res);
        setIsEditing(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Delete this resource?")) {
            try {
                await deleteDoc(doc(db, 'cases', id));
                fetchResources();
                // Trigger Sitemap Update
                await triggerAutoUpdate();
            } catch (error) {
                console.error("Error deleting resource:", error);
            }
        }
    };

    return (
        <div className="panel active">
            <div className="page-hdr">
                <div className="page-hdr-left">
                    <div className="eyebrow">Content</div>
                    <h1>Resources & Case Studies <span>.</span></h1>
                </div>
                <div className="page-hdr-actions">
                    <Link to="/admin/dashboard" className="btn btn-ghost" style={{ borderColor: 'var(--border)', color: 'var(--text2)', marginRight: '8px' }}>
                        Dashboard
                    </Link>
                    <button className="btn btn-ghost" onClick={seedResources}>
                        <ExternalLink size={14} /> Seed Data
                    </button>
                    <button className="btn btn-primary" onClick={() => { setCurrentResource(null); setFormData(initialResourceState); setIsEditing(true); }}>
                        <Plus size={14} /> Add Resource
                    </button>
                </div>
            </div>

            {!isEditing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {isLoading ? (
                        <div className="col-span-full text-center py-10 text-gray-500">Loading resources...</div>
                    ) : resources.length === 0 ? (
                        <div className="col-span-full text-center py-10 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
                            No resources found.
                        </div>
                    ) : (
                        resources.map(res => (
                            <div key={res.id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-all group">
                                <div className="h-40 bg-gray-100 relative">
                                    {res.image ? (
                                        <img src={res.image} alt={res.title} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                                            <ImageIcon size={32} />
                                        </div>
                                    )}
                                    <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => handleEdit(res)} className="p-2 bg-white/90 rounded-lg shadow-sm hover:bg-white text-blue-600">
                                            <Edit2 size={14} />
                                        </button>
                                        <button onClick={() => handleDelete(res.id)} className="p-2 bg-white/90 rounded-lg shadow-sm hover:bg-white text-red-600">
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                    <span className="absolute bottom-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded backdrop-blur-sm">
                                        {res.category}
                                    </span>
                                </div>
                                <div className="p-5">
                                    <h3 className="font-bold text-gray-900 mb-2 line-clamp-1">{res.title}</h3>
                                    <p className="text-sm text-gray-500 line-clamp-2 mb-4">{res.summary}</p>
                                    <div className="flex justify-between items-center mt-4">
                                        <span className="text-xs text-gray-400">{res.date_published}</span>
                                        {res.file_url && (
                                            <a href={res.file_url} target="_blank" rel="noopener noreferrer" className="text-brand-accent hover:underline text-xs flex items-center gap-1">
                                                View File <ExternalLink size={10} />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            ) : (
                <div className="max-w-3xl mx-auto bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-bold">{currentResource ? 'Edit Resource' : 'New Resource'}</h2>
                        <button onClick={() => setIsEditing(false)} className="btn btn-ghost"><X size={16} /></button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-brand-accent focus:outline-none"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                <select
                                    name="category"
                                    value={formData.category}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-brand-accent focus:outline-none"
                                >
                                    <option value="Case Study">Case Study</option>
                                    <option value="Whitepaper">Whitepaper</option>
                                    <option value="Calculator">Calculator Tool</option>
                                    <option value="Blog">Blog Post</option>
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Summary / Description (Short)</label>
                            <textarea
                                name="summary"
                                value={formData.summary}
                                onChange={handleInputChange}
                                rows="2"
                                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-brand-accent focus:outline-none"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Content / Article</label>
                            <textarea
                                name="content"
                                value={formData.content || ''}
                                onChange={handleInputChange}
                                rows="8"
                                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-brand-accent focus:outline-none font-mono text-sm"
                                placeholder="Write the full case study or article here..."
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Cover Image</label>
                            <div className="flex items-center gap-4">
                                {formData.image && (
                                    <img src={formData.image} alt="Preview" className="w-20 h-12 object-cover rounded border" />
                                )}
                                <label className="cursor-pointer btn btn-outline btn-sm flex items-center gap-2">
                                    {uploading ? <Loader className="animate-spin" size={14} /> : <Upload size={14} />}
                                    Upload
                                    <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, 'image')} disabled={uploading} />
                                </label>
                                <input
                                    type="text"
                                    name="image"
                                    value={formData.image}
                                    onChange={handleInputChange}
                                    placeholder="or Image URL"
                                    className="flex-1 p-2 border rounded-lg text-sm"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Resource File (PDF) or Link</label>
                            <div className="flex items-center gap-4">
                                <label className="cursor-pointer btn btn-outline btn-sm flex items-center gap-2">
                                    {uploading ? <Loader className="animate-spin" size={14} /> : <FileText size={14} />}
                                    Upload PDF
                                    <input type="file" className="hidden" accept=".pdf" onChange={(e) => handleFileUpload(e, 'file_url')} disabled={uploading} />
                                </label>
                                <input
                                    type="text"
                                    name="file_url"
                                    value={formData.file_url}
                                    onChange={handleInputChange}
                                    placeholder="or External Link URL"
                                    className="flex-1 p-2 border rounded-lg text-sm"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t">
                            <button type="button" onClick={() => setIsEditing(false)} className="btn btn-ghost">Cancel</button>
                            <button type="submit" className="btn btn-primary" disabled={isLoading || uploading}>
                                {isLoading ? <Loader className="animate-spin" size={14} /> : <Save size={14} />}
                                {currentResource ? 'Update Resource' : 'Create Resource'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default ResourceManager;
